import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import Iconify from "src/components/Iconify";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { restrict } from "src/components/restrict";
import { addPartyOrder, masterGetData, masterSaveData } from "src/pages/api";

import * as Yup from "yup";

const defaultArrayProduct = {
  productID: "",
  isbn: "",
  qty: "1",
  singleQtyPrice: 0,
  totalPrice: 0,
};

const defaultValues = {
  partyID: null,
  products: [defaultArrayProduct],
};

const productSchema = Yup.object().shape({
  productID: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  // optional: Yup.string().trim().required("Required"),
  qty: Yup.number()
    .nullable()
    .typeError("Required")
    .required("required")
    .min(1, "Minimum Value should be 1"),
});

const schema = Yup.object().shape({
  products: Yup.array().of(productSchema),
  partyID: Yup.object()
    .shape()
    .typeError("Required")
    .required("Required")
    .nullable(),
});
export default function CreateOutWardOrder({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { handleSubmit, reset, control, watch, setValue, getValues } = methods;

  const { fields, append, remove } = useFieldArray({
    name: "products",
    control,
  });

  const { data } = useQuery({
    queryKey: ["getData", "products", 0, 5000],
    queryFn: masterGetData,
  });

  const { data: partyMasterList } = useQuery({
    queryKey: ["getData", "party_master", 0, 100],
    queryFn: masterGetData,
  });

  const mutation = useMutation(req => addPartyOrder({ req }), {
    onSuccess: () => {
      enqueueSnackbar(`Created Order successfully`, {
        variant: "success",
      });
      queryClient.invalidateQueries(["getAllPartyOrder"]);

      onClose();
    },
    onError: error => {
      enqueueSnackbar(error?.message || "Failed to Add", { variant: "error" });
    },
  });

  const calculatePrice = index => {
    const singlePrice = watch(`products.${index}.singleQtyPrice`) || 0;
    const qty = watch(`products.${index}.qty`) || 0;
    setValue(`products.${index}.totalPrice`, singlePrice * qty || 0);
  };
  const calculatTotal = () => {
    let amt = 0;
    watch("products")?.forEach(el => {
      if (el.qty && el.productID) {
        amt += +el.qty * el.singleQtyPrice;
      }
    });
    setTotalPrice(amt);
  };

  const onSubmit = data => {
    const req = {
      partyID: data.partyID.id,
      orderType: "product",
      items: [
        {
          type: "product",
          products: data.products.map(el => ({
            id: el.productID.id,
            qty: el.qty,
          })),
        },
      ],
    };
    mutation.mutate(req);
  };

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={
        !isEditMode &&
        (() => {
          setTotalPrice(0);
          reset();
        })
      }
      loading={mutation.isLoading}
      maxWidth={"md"}
      fullWidth
      fullScreen
      isEditMode={isEditMode}
      title={"Create Outward Order"}
      disabled={watch("products")?.length === 0}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <RHFAutocomplete
              name={"partyID"}
              label={"Party"}
              option={partyMasterList?.data || []}
              placeholder={"Select Party"}
              loading={loading}
              size="small"
              required
              getOptionLabel={option => option?.name}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          </Grid>
        </Grid>

        <TableContainer>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={70}>Sr. No.</TableCell>
                <TableCell width={250}>Product</TableCell>
                <TableCell width={250}>ISBN</TableCell>
                <TableCell width={150}>Qty</TableCell>
                <TableCell width={180}>Single Qty Price</TableCell>
                <TableCell width={150}>Total</TableCell>
                {/* <TableCell>optional</TableCell>
                  <TableCell width={150}>Min Qty</TableCell>
                  <TableCell width={150}>Max Qty</TableCell> */}
                <TableCell>
                  {" "}
                  <IconButton
                    size="small"
                    color="info"
                    title="Add New"
                    onClick={() => {
                      append(defaultArrayProduct);
                    }}
                  >
                    <Iconify icon={"eva:plus-fill"} />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <RHFAutocomplete
                      name={`products.${index}.productID`}
                      native={false}
                      fullWidth
                      size="small"
                      option={data?.data || []}
                      getOptionDisabled={option =>
                        watch("products").some(
                          item => item?.productID?.id === option.id
                        )
                      }
                      getOptionLabel={option => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onInputChange={value => {
                        setValue(
                          `products.${index}.singleQtyPrice`,
                          value?.price || 0
                        );
                        setValue(`products.${index}.isbn`, value?.isbn || "");

                        calculatePrice(index);
                        calculatTotal();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`products.${index}.isbn`}
                      // label=""
                      placeholder="ISBN"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5",
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`products.${index}.qty`}
                      label="Qty"
                      size="small"
                      inputProps={{ maxLength: 5 }}
                      onInput={restrict.digits}
                      onInputChange={() => {
                        calculatePrice(index);
                        calculatTotal();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`products.${index}.singleQtyPrice`}
                      // label=""
                      placeholder="Single Qty Price"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5",
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                      onInput={restrict.digits}
                    />
                  </TableCell>
                  <TableCell>
                    <RHFTextField
                      name={`products.${index}.totalPrice`}
                      // label="Total"
                      placeholder="Total"
                      size="small"
                      inputProps={{
                        readOnly: true,
                        style: {
                          backgroundColor: "#f5f5f5",
                          color: "rgba(0, 0, 0, 0.38)", 
                          cursor: "default",
                        },
                      }}
                      onInput={restrict.digits}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      title="Delete"
                      onClick={() => {
                        remove(index);
                      }}
                      disabled={index === 0 && !isEditMode}
                    >
                      <Iconify icon={"eva:trash-2-outline"} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TextField
          label="Total Price"
          value={totalPrice}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            readOnly: true,
            style: {
              backgroundColor: "#f5f5f5",
              color: "rgba(0, 0, 0, 0.38)", 
              cursor: "default",
            },
          }}
          size="small"
          sx={{ mt: 2, float: "right" }}
        />
      </FormProvider>
    </FormWrapper>
  );
}
